import { classNames } from '@fullstory/util/fmt';
import React, { PropsWithChildren, useRef } from 'react';
import { Overlay } from '../../legacy/Overlay';
import { Slide } from '../../legacy/Slide';
import { identifyComponent } from '../../util';
import css, { ClassNames } from './index.module.scss';

type SidecarPosition = 'right' | 'left';

export interface SidecarProps {
  testId?: string;

  /** Either `right` or `left` that represents where the sidecar will be placed */
  position: SidecarPosition;

  /** Callback that is called immediately when a close event happens.  */
  onClose: () => void;

  /** Callback that is called after the sidecar animates out. */
  onClosed?: () => void;

  /** Determines whether the sidecar is shown or not. */
  open: boolean;

  /**
   * Determines the width of the sidecar.
   */
  width?: 'md' | 'lg' | 'xl';
}

/**
 * A sidecar is used to open and close a panel that extends and retracts out of the side of the
 * window. The panel is the entire height of the viewport.
 *
 * Note: Do not conditionally render the sidecar based on some state - instead use the `open` prop.
 * E.G. Do not do `{open && <Sidecar open={open} />} - this will break the animations.
 */
export const Sidecar: React.FC<PropsWithChildren<SidecarProps>> = ({
  testId,
  position,
  children,
  open,
  onClose,
  onClosed,
  width = 'md',
}) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  return (
    <Overlay data-component="Sidecar" open={open} onClose={onClose} overflowY="hidden">
      <Slide in={open} position={position} onTransitionEnd={onClosed} nodeRef={nodeRef}>
        <div
          ref={nodeRef}
          data-testid={testId}
          className={classNames(
            'fs_component_sidecar',
            css.sidecar,
            css[position],
            css[`width-${width}` as ClassNames],
          )}
          {...identifyComponent('sidecar')}
        >
          <div className={css.container}>{children}</div>
        </div>
      </Slide>
    </Overlay>
  );
};
